/**
 * Predictive Insights – forecast, anomalies, booking trend
 */

function linearRegression(values) {
  const n = values.length;
  if (n < 2) return { slope: 0, intercept: values[0] || 0 };
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
  values.forEach((y, index) => {
    const x = index;
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  });
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

function getRevenue(item) {
  return parseFloat(item.totalPrice || item.price || item.revenue || 0) || 0;
}

export function forecastRevenue(historicalData, periodsAhead = 1) {
  if (!Array.isArray(historicalData) || historicalData.length < 2) {
    return {
      forecast: 0,
      confidenceInterval: { lower: 0, upper: 0 },
      trend: 'stable',
    };
  }
  const revenues = historicalData.map(getRevenue);
  const { slope, intercept } = linearRegression(revenues);
  const nextIndex = revenues.length;
  const forecast = slope * nextIndex + intercept;
  const mean = revenues.reduce((a, b) => a + b, 0) / revenues.length;
  const variance = revenues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / revenues.length;
  const stdDev = Math.sqrt(variance);
  const margin = stdDev * 2;
  const trend = slope > 0.1 ? 'increasing' : slope < -0.1 ? 'decreasing' : 'stable';
  return {
    forecast: Math.max(0, forecast),
    confidenceInterval: {
      lower: Math.max(0, forecast - margin),
      upper: forecast + margin,
    },
    trend,
  };
}

export function detectAnomalies(data, metric = 'revenue') {
  if (!Array.isArray(data) || data.length < 3) return [];
  const values = data.map((item) => {
    if (metric === 'revenue') return getRevenue(item);
    if (metric === 'bookings') return 1;
    return parseFloat(item.value || 0) || 0;
  });
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
  const stdDev = Math.sqrt(variance);
  const threshold = 2 * stdDev;
  const anomalies = [];
  values.forEach((value, index) => {
    const deviation = Math.abs(value - mean);
    if (deviation > threshold) {
      anomalies.push({
        index,
        value,
        dataPoint: data[index],
        deviation,
        type: value > mean ? 'spike' : 'drop',
        severity: deviation > 3 * stdDev ? 'high' : 'medium',
      });
    }
  });
  return anomalies;
}

export function predictBookingTrend(bookingData, daysAhead = 7) {
  if (!Array.isArray(bookingData) || bookingData.length < 2) {
    return {
      predictedBookings: 0,
      trend: 'stable',
      confidence: 'low',
      averageBookings: 0,
      change: 0,
    };
  }
  const bookingsByDate = {};
  bookingData.forEach((item) => {
    const date = item.selectedDate || item.date || item.createdAt;
    if (date) {
      const d = date instanceof Date ? date : new Date(date);
      const dateKey = d.toISOString ? d.toISOString().split('T')[0] : String(date).slice(0, 10);
      bookingsByDate[dateKey] = (bookingsByDate[dateKey] || 0) + 1;
    }
  });
  const dates = Object.keys(bookingsByDate).sort();
  const counts = dates.map((d) => bookingsByDate[d]);
  const { slope, intercept } = linearRegression(counts);
  const nextIndex = counts.length;
  const predictedBookings = Math.max(0, Math.round(slope * nextIndex + intercept));
  const avgBookings = counts.reduce((a, b) => a + b, 0) / counts.length;
  const trend = slope > 0.1 ? 'increasing' : slope < -0.1 ? 'decreasing' : 'stable';
  const confidence = counts.length >= 7 ? 'high' : counts.length >= 3 ? 'medium' : 'low';
  return {
    predictedBookings,
    trend,
    confidence,
    averageBookings: Math.round(avgBookings),
    change: predictedBookings - Math.round(avgBookings),
  };
}
